import { PlayerDetails, PlayerHealth } from "@/lib/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  MonitorIcon,
  WifiIcon,
  TimerIcon,
  MapPinIcon,
  AlertCircleIcon,
  CopyIcon,
  ClockIcon,
  ActivityIcon,
  ServerIcon,
  RefreshCcwIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { showSuccess, showApiError } from "@/lib/toast";
import { getArea, getPlayerHealth, getScreenSize, restartPlayer } from "@/sideEffects/api";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { SelectedMediaPreview } from "@/pages/shared/SelectedMediaPreview";
import { useTranslation } from "react-i18next";
import { DashboardPlayerHealthStatus } from "@/pages/dashboard/DashboardPlayerHealthStatus";
import { formatDateToLocal } from "@/lib/dateUtils";
import { ConfirmActionDialog, ConfirmActionDialogRef } from "@/components/ConfirmActionDialog";
import { useRef } from "react";

interface Props {
  playerDetails: PlayerDetails;
}

type TimeSettingsFormValues = {
  openingTime: string;
  closingTime: string;
};

export const PlayerInformationPage = ({ playerDetails }: Props) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const dialogRef = useRef<ConfirmActionDialogRef>(null);

  // Create the schema inside the component where t is available
  const timeSettingsSchema = z.object({
    openingTime: z.string().min(1, t("playerInfo.openingTimeRequired")),
    closingTime: z.string().min(1, t("playerInfo.closingTimeRequired")),
  });

  const { data: playerHealth } = useQuery({
    queryKey: ["getPlayerHealth", playerDetails?.intId],
    queryFn: () => getPlayerHealth(Number(playerDetails?.intId), String(activeFirm?.firmGuid)),
    enabled: Boolean(playerDetails.intId),
    refetchInterval: 10 * 1000,
  });

  const health = playerHealth?.data.health;

  const form = useForm<TimeSettingsFormValues>({
    resolver: zodResolver(timeSettingsSchema),
    defaultValues: {
      openingTime: "08:00",
      closingTime: "20:00",
    },
  });

  const queryClient = useQueryClient();

  const restartPlayerMutation = useMutation({
    mutationFn: () => restartPlayer(String(playerDetails.id), String(activeFirm?.firmGuid)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getPlayerDetails"],
      });
      showSuccess(t("common.restartedSuccess"));
    },
    onError: showApiError,
  });

  const onSubmitTimeSettings = (data: TimeSettingsFormValues) => {
    console.log("Time settings updated:", data);
    // Here you would make an API call to update the time settings
  };

  const { data: areas } = useQuery({
    queryKey: ["getAreas"],
    queryFn: () => getArea(String(activeFirm?.firmGuid)),
  });

  const { data: screenSizes } = useQuery({
    queryKey: ["getScreenSizes"],
    queryFn: () =>
      getScreenSize({ pageNumber: 1, numberOfObjectsPerPage: 99999 }, { Firm_Id: String(activeFirm?.firmGuid) }),
  });

  const areaName = areas?.data.result.find((area) => area.id === playerDetails.areaId)?.name || t("common.notSet");
  const screenSizeName =
    screenSizes?.data.result.find((screenSize) => screenSize.intId === playerDetails.screenSizeintId)?.name ||
    t("common.notSet");

  return (
    <div className="space-y-6">
      {/* Header with player name and status */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 shadow-lg mt-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="bg-white p-3 rounded-full shadow-md">
              <MonitorIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{playerDetails.name}</h1>
              <div className="flex items-center mt-1">
                {health === PlayerHealth.ok && <div className={`h-3 w-3 rounded-full bg-green-400 mr-2`}></div>}
                {health === PlayerHealth.unhealthy && <div className={`h-3 w-3 rounded-full bg-red-400 mr-2`}></div>}
                {health === PlayerHealth.uninitialized && (
                  <div className={`h-3 w-3 rounded-full bg-gray-400 mr-2`}></div>
                )}
                <p className="text-white text-opacity-90">
                  {health === PlayerHealth.ok && t("playerInfo.healthEnum.1")}
                  {health === PlayerHealth.unhealthy && t("playerInfo.healthEnum.2")}
                  {health === PlayerHealth.uninitialized && t("playerInfo.healthEnum.0")}
                </p>
              </div>
            </div>
          </div>
          <div className="flex space-x-4">
            <ConfirmActionDialog
              ref={dialogRef}
              title={t("playerInfo.restartScreenConfirmTitle")}
              description={t("playerInfo.restartScreenConfirmDesc")}
              confirmButtonText={t("playerInfo.restartScreen")}
              trigger={
                <Button variant="outline" className="bg-white text-blue-600 hover:bg-blue-50 shadow-md" size="sm">
                  {t("playerInfo.restartScreen")} <RefreshCcwIcon className="w-4 h-4" />
                </Button>
              }
              onConfirm={() => restartPlayerMutation.mutate()}
              isLoading={restartPlayerMutation.isPending}
            />
            {false && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-white text-purple-600 hover:bg-purple-50 shadow-md">
                    <TimerIcon className="mr-2 h-4 w-4" />
                    {t("playerInfo.setOpeningClosingTime")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>{t("playerInfo.screenSchedule")}</DialogTitle>
                    <DialogDescription>{t("playerInfo.screenScheduleDesc")}</DialogDescription>
                  </DialogHeader>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmitTimeSettings)} className="space-y-4">
                      <FormField
                        control={form.control}
                        name="openingTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("playerInfo.openingTime")}</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} />
                            </FormControl>
                            <FormDescription>{t("playerInfo.openingTimeDesc")}</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="closingTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("playerInfo.closingTime")}</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} />
                            </FormControl>
                            <FormDescription>{t("playerInfo.closingTimeDesc")}</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <DialogFooter>
                        <Button type="submit">{t("playerInfo.saveChanges")}</Button>
                      </DialogFooter>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center space-x-4">
            <div className="bg-blue-100 p-3 rounded-full">
              <ClockIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-600">{t("playerInfo.lastPublished")}</p>
              <p className="text-lg font-semibold">
                {playerDetails.lastPublish === "N/A"
                  ? playerDetails.lastPublish
                  : formatDateToLocal(playerDetails.lastPublish)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center space-x-4">
            <div className="bg-purple-100 p-3 rounded-full">
              <ServerIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-purple-600">{t("playerInfo.ipAddress")}</p>
              <p className="text-lg font-semibold">{playerDetails.ipAddress || "-"}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center space-x-4">
            <div className="bg-green-100 p-3 rounded-full">
              <MapPinIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-600">{t("entities.area")}</p>
              <p className="text-lg font-semibold">{areaName || "-"}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center space-x-4">
            <div className="bg-amber-100 p-3 rounded-full">
              <ActivityIcon className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-amber-600">{t("entities.screenSize")}</p>
              <p className="text-lg font-semibold">{screenSizeName || "-"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Screen Identification Card */}
        <Card className="border-l-4 border-l-blue-500 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2 bg-gradient-to-r from-blue-50 to-transparent">
            <CardTitle className="flex items-center text-blue-700">
              <MonitorIcon className="mr-2 h-5 w-5 text-blue-500" />
              {t("playerInfo.screenIdentification")}
            </CardTitle>
            <CardDescription>{t("playerInfo.screenIdentificationDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-blue-600">{t("playerInfo.lastPublished")}</p>
                <p className="text-sm font-semibold">
                  {playerDetails.lastPublish === "N/A"
                    ? playerDetails.lastPublish
                    : formatDateToLocal(playerDetails.lastPublish)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-600">{t("common.code")}</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold bg-blue-50 p-2 rounded-md border border-blue-200 border-dashed flex-1">
                    {playerDetails.code}
                  </p>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(playerDetails.code);
                      showSuccess(t("common.copied"));
                    }}
                    className="flex-shrink-0 text-blue-600 border-blue-200 hover:bg-blue-50"
                    title={t("common.copy")}
                  >
                    <CopyIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Network Information Card */}
        <Card className="border-l-4 border-l-purple-500 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2 bg-gradient-to-r from-purple-50 to-transparent">
            <CardTitle className="flex items-center text-purple-700">
              <WifiIcon className="mr-2 h-5 w-5 text-purple-500" />
              {t("playerInfo.networkInformation")}
            </CardTitle>
            <CardDescription>{t("playerInfo.networkInformationDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-purple-600">{t("playerInfo.ipAddress")}</p>
                <p className="text-sm font-semibold">{playerDetails.ipAddress}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-purple-600">{t("playerInfo.timezone")}</p>
                <p className="text-sm font-semibold">{playerDetails.timezone}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-purple-600">{t("licence.versionString")}</p>
                <p className="text-sm font-semibold">{playerDetails.versionString}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Information Card */}
        <Card className="border-l-4 border-l-green-500 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-transparent">
            <CardTitle className="flex items-center text-green-700">
              <AlertCircleIcon className="mr-2 h-5 w-5 text-green-500" />
              {t("playerInfo.statusInformation")}
            </CardTitle>
            <CardDescription>{t("playerInfo.statusInformationDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-green-600">{t("playerInfo.healthStatus")}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <DashboardPlayerHealthStatus playerId={playerDetails.intId} />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-green-600">{t("playerInfo.deviceType")}</p>
                <p className="text-sm font-semibold">{playerDetails.deviceType || "New"}</p>
              </div>
            </div>

            <Separator className="bg-green-100" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-green-600">{t("content.defaultContent")}</p>
                <div className="mt-2 flex flex-wrap gap-2 bg-green-50 p-2 rounded-md border border-green-100">
                  <SelectedMediaPreview contentIntId={playerDetails.defaultContentintId} />
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-green-600">{t("common.media")}</p>
                <div className="mt-2 flex flex-wrap gap-2 bg-green-50 p-2 rounded-md border border-green-100">
                  <SelectedMediaPreview
                    channelIntId={playerDetails.channelintId}
                    contentGroupIntId={playerDetails.contentGroupintId}
                    contentIntId={playerDetails.contentintId}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Information Card */}
        <Card className="border-l-4 border-l-amber-500 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2 bg-gradient-to-r from-amber-50 to-transparent">
            <CardTitle className="flex items-center text-amber-700">
              <MapPinIcon className="mr-2 h-5 w-5 text-amber-500" />
              {t("playerInfo.locationAndArea")}
            </CardTitle>
            <CardDescription>{t("playerInfo.locationAndAreaDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-amber-600">{t("entities.area")}</p>
                <p className="text-sm font-semibold">{areaName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-amber-600">{t("entities.screenSize")}</p>
                <p className="text-sm font-semibold">{screenSizeName}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm font-medium text-amber-600">{t("player.location")}</p>
                {playerDetails.latitude && playerDetails.longitude ? (
                  <div className="mt-2 bg-amber-50 p-2 rounded-md border border-amber-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-700">Lat: {playerDetails.latitude}</p>
                        <p className="text-xs text-amber-700">Long: {playerDetails.longitude}</p>
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${playerDetails.latitude},${playerDetails.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-amber-100 text-amber-700 px-3 py-1 rounded-md hover:bg-amber-200 transition-colors flex items-center"
                      >
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        {t("location.showOnMap")}
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 bg-amber-50 p-2 rounded-md border border-amber-100 text-amber-700">
                    {t("location.noLocationSet")}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
